function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
import './CTAVariation.css';
import React, { useState, useMemo, useCallback, Fragment } from 'react';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import { SitecoreContextReactContext } from '../../utils/SitecoreContextReactContext';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { convertToAnalyticObject } from '../../utils/analytics';
import { get, getPageThemeColors, getColorByTheme, canUseDOM, getDictionaryItem } from '../../utils';
import HappinessMeterWidget from '../HappinessMeterWidget/HappinessMeterWidget';
import CodeSnippet from '../CodeSnippet/CodeSnippet';
import { useScroll } from './useScroll';
import RouterLink from '../atoms/RouterLink';
import { useModal } from '../atoms/Modal/useModal';
import Modal from '../atoms/Modal/Modal';
const transition = '0.3s linear';
const CTAVariation = _ref => {
  let {
    fields,
    children,
    params
  } = _ref;
  const sitecoreContext = React.useContext(SitecoreContextReactContext);
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const rightLinks = get(fields, 'data.item.rightLinks.targetItems') || [];
  const leftLinks = get(fields, 'data.item.leftLinks.targetItems') || [];
  const centerLinks = get(fields, 'data.item.centerLinks.targetItems') || [];
  const dictionary = get(fields, 'data.dictionary', {
    dictionaryValues: []
  });
  const happinessMeterText = getDictionaryItem(dictionary, 'happinessMeter') || '';
  const {
    isShown,
    toggle
  } = useModal();
  const [iframeUrl, setIframeUrl] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [isPanelFocused, setIsPanelFocused] = useState(false);
  const isAlwaysBeVisible = get(fields, 'data.item.alwaysBeVisible.jss.value') || false;
  const accessibilitySettings = get(fields, 'data.accessibilitySettings');
  const analytics = get(fields, 'data.analytics');
  const activeLanguage = get(fields, 'data.activeLanguage');
  const stickyBannerRef = React.useRef(null);
  const scrollDown = useScroll(50);
  React.useEffect(() => {
    if (scrollDown) {
      setIsPanelFocused(false);
    }
  }, [scrollDown]);
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const [isVdsite, setIsVdsite] = React.useState(true);
  React.useEffect(() => {
    if (canUseDOM && !document.body.classList.contains('vdsite')) {
      setIsVdsite(false);
    }
  }, []);
  React.useEffect(() => {
    if (canUseDOM && isAlwaysBeVisible && stickyBannerRef.current) {
      const handleLoad = () => {
        const stickyBannerHeight = stickyBannerRef.current.offsetHeight;
        /**
         * add additional offset for using on another element (for example, sticky banner) to avoid overlap with sticky CTA
         * add additional class .sticky-cta-visible for checking if sticky CTA is visible and use it in css
         */
        document.body.style.setProperty('--sticky-offset', `${stickyBannerHeight}px`);
        document.body.classList.add('sticky-cta-visible');
      };
      window.addEventListener('load', handleLoad);
      return () => {
        window.removeEventListener('load', handleLoad);
      };
    }
  }, [isAlwaysBeVisible, stickyBannerRef]);
  const pageThemeColors = useMemo(() => getPageThemeColors(isVdsite), [isVdsite]);
  const BackgroundColorHex = get(params, 'BackgroundColorHex');
  const ColorHex = get(params, 'ColorHex');
  const ActiveColorHex = get(params, 'ActiveColorHex');
  const primaryPageThemeColor = get(sitecoreContext, 'themesOfPage.primary');
  const primaryActivePageThemeColor = get(sitecoreContext, 'themesOfPage.primary_active');
  const selectedPrimaryColor = primaryPageThemeColor ? primaryPageThemeColor : pageThemeColors.primary;
  const [state, setState] = useState({
    accessibilitySettingsVisible: false
  });
  const AccessibilitySettingsComponent = state.accessibilitySettingsComponent;
  const setHTMLScroll = React.useCallback(_ref2 => {
    let {
      disabled
    } = _ref2;
    if (typeof window === undefined) return;
    const html = document.querySelector('html');
    if (html) html.style.overflow = disabled ? 'hidden' : '';
  }, []);
  const toggleAccessibilitySettingsVisibility = useCallback(() => {
    const newState = {
      accessibilitySettingsVisible: !state.accessibilitySettingsVisible
    };
    setHTMLScroll({
      disabled: newState.accessibilitySettingsVisible
    });
    if (!state.accessibilitySettingsComponent) {
      import(/* webpackChunkName: "AccessibilitySettings" */'../30_NavigationBar/AccessibilitySettings').then(component => {
        newState.accessibilitySettingsComponent = component.default;
        setState(newState);
      });
    } else {
      setState(newState);
    }
  }, [state.accessibilitySettingsVisible, state.accessibilitySettingsComponent]);
  const checkAccessibilitySettings = () => {
    if (Boolean(accessibilitySettings) && Boolean(analytics) && Boolean(activeLanguage)) {
      return true;
    }
    return false;
  };
  const colorByTheme = useMemo(() => {
    return getColorByTheme(selectedPrimaryColor);
  }, [selectedPrimaryColor]);
  const variables = BackgroundColorHex ? {
    '--m113-cta-variation-background-color': BackgroundColorHex,
    '--m113-cta-variation-background-active-color': ActiveColorHex,
    '--m113-cta-variation-color': ColorHex
  } : {
    '--m113-cta-variation-background-color': selectedPrimaryColor,
    '--m113-cta-variation-background-active-color': primaryActivePageThemeColor ? primaryActivePageThemeColor : pageThemeColors.primary_active,
    '--m113-cta-variation-color': colorByTheme
  };
  if (fields && !fields.data) {
    console.info('CTAVariation: check fields.data settings');
    return /*#__PURE__*/React.createElement("div", {
      className: "no-data"
    }, " [CTAVariation component: something went wrong]");
  }
  if (fields && fields.data && !fields.data.item) {
    console.info('CTAVariation: check fields.data.item settings');
    return /*#__PURE__*/React.createElement("div", {
      className: "no-data"
    }, " [CTAVariation component: something went wrong]");
  }
  if (!fields) {
    console.info('CTAVariation component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", {
        className: "no-data"
      }, " [CTAVariation component is empty: please provide data source]");
    }
    return null;
  }
  const renderLink = (el, placement) => {
    const id = get(el, 'id');
    const title = get(el, 'title.jss.value');
    const tooltipText = get(el, 'toolTip.jss.value') || '';
    const name = get(el, 'name') || 'name is not provided';
    const icon = get(el, 'svgIcon.targetItem.svg.jss.value');
    const linkHref = get(el, 'link.jss.value.href');
    const linkType = get(el, 'link.jss.value.linktype');
    const linkTarget = get(el, 'link.jss.value.target');
    const isPopup = get(el, 'isPopup.jss.value') || false;
    const code = get(el, 'code.jss.value');
    const isAccessibility = get(el, 'template.name') === 'CTA Accessibility Item' || false;
    const isDesktop = get(el, 'isDesktop.jss.value') || false;
    const iframeUrl = get(el, 'iFrameLink.jss.value') || '';
    const isIframe = Boolean(iframeUrl) || false;
    const tooltipLocationForPopup = {
      right: `${rtl ? 'right' : 'left'}`,
      left: `${rtl ? 'left' : 'right'}`,
      center: 'top'
    };
    if (isPopup) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx('m113-sticky-banner-card', 'm113-sticky-banner-card--code-snippet', {
          'm113-sticky-banner-card--desktop-only': isDesktop
        }),
        "data-tooltip-id": `tooltip-cd-${id}-${placement}`
      }, /*#__PURE__*/React.createElement("button", {
        className: "m113-sticky-banner-link chat",
        key: id,
        onClick: () => {
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['openPopupAnalytics'], params), name);
        },
        onFocus: () => setIsPanelFocused(true)
      }, icon ? /*#__PURE__*/React.createElement("span", {
        dangerouslySetInnerHTML: {
          __html: icon
        },
        className: "icon"
      }) : /*#__PURE__*/React.createElement(React.Fragment, null), title ? /*#__PURE__*/React.createElement("span", {
        className: "m113-card__title"
      }, title) : /*#__PURE__*/React.createElement(React.Fragment, null), /*#__PURE__*/React.createElement(CodeSnippet, {
        fields: {
          data: {
            item: {
              name: title,
              code: {
                jss: {
                  value: code
                }
              },
              scripts: {
                jss: {
                  value: ''
                }
              }
            }
          }
        },
        params: params
      })), tooltipText ? /*#__PURE__*/React.createElement(Tooltip, {
        id: `tooltip-cd-${id}-${placement}`,
        place: tooltipLocationForPopup[placement],
        className: "m113-sticky-banner-card-tooltip"
      }, /*#__PURE__*/React.createElement("span", null, tooltipText)) : null);
    } else if (isIframe) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx('m113-sticky-banner-card', 'm113-sticky-banner-card--iframe', {
          'm113-sticky-banner-card--desktop-only': isDesktop
        }),
        "data-tooltip-id": `tooltip-iframe-${id}-${placement}`
      }, /*#__PURE__*/React.createElement("button", {
        className: "m113-sticky-banner-link",
        "aria-label": title || tooltipText || name,
        key: id,
        onClick: () => {
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['openPopupAnalytics'], params), name);
          setIframeUrl(iframeUrl);
          toggle();
        },
        onFocus: () => setIsPanelFocused(true)
      }, icon ? /*#__PURE__*/React.createElement("span", {
        dangerouslySetInnerHTML: {
          __html: icon
        },
        className: "icon"
      }) : /*#__PURE__*/React.createElement(React.Fragment, null), title ? /*#__PURE__*/React.createElement("span", {
        className: "m113-card__title"
      }, title) : /*#__PURE__*/React.createElement(React.Fragment, null)), tooltipText ? /*#__PURE__*/React.createElement(Tooltip, {
        id: `tooltip-iframe-${id}-${placement}`,
        place: tooltipLocationForPopup[placement],
        className: "m113-sticky-banner-card-tooltip"
      }, /*#__PURE__*/React.createElement("span", null, tooltipText)) : null);
    } else if (isAccessibility) {
      return checkAccessibilitySettings() ? /*#__PURE__*/React.createElement("div", {
        className: clsx('m113-sticky-banner-card', 'm113-sticky-banner-card--accessibility', {
          'm113-sticky-banner-card--desktop-only': isDesktop
        }),
        "data-tooltip-id": `tooltip-accessibility-${id}-${placement}`
      }, /*#__PURE__*/React.createElement("button", {
        className: "m113-sticky-banner-link",
        "aria-label": title || tooltipText || name,
        key: id,
        onClick: () => {
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['openPopupAnalytics'], params), name);
          toggleAccessibilitySettingsVisibility();
        },
        onFocus: () => setIsPanelFocused(true)
      }, icon ? /*#__PURE__*/React.createElement("span", {
        dangerouslySetInnerHTML: {
          __html: icon
        },
        className: "icon"
      }) : /*#__PURE__*/React.createElement(React.Fragment, null), title ? /*#__PURE__*/React.createElement("span", {
        className: "m113-card__title"
      }, title) : /*#__PURE__*/React.createElement(React.Fragment, null)), tooltipText ? /*#__PURE__*/React.createElement(Tooltip, {
        id: `tooltip-accessibility-${id}-${placement}`,
        place: tooltipLocationForPopup[placement],
        className: "m113-sticky-banner-card-tooltip"
      }, /*#__PURE__*/React.createElement("span", null, tooltipText)) : null) : null;
    } else {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx('m113-sticky-banner-card', {
          'm113-sticky-banner-card--desktop-only': isDesktop
        }),
        "data-tooltip-id": `tooltip-link-${id}-${placement}`
      }, /*#__PURE__*/React.createElement(RouterLink, {
        className: "m113-sticky-banner-link",
        href: linkHref,
        key: id,
        onClick: () => {
          tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['clickAnalytics'], params), name);
        },
        dataCy: "multifunctional-card-link",
        linktype: linkType,
        linksTarget: linkTarget,
        onFocus: () => setIsPanelFocused(true),
        ariaLabel: `Sticky banner link - ${title}`
      }, icon ? /*#__PURE__*/React.createElement("span", {
        dangerouslySetInnerHTML: {
          __html: icon
        },
        className: "icon"
      }) : /*#__PURE__*/React.createElement(React.Fragment, null), title ? /*#__PURE__*/React.createElement("span", {
        className: "m113-card__title"
      }, title) : /*#__PURE__*/React.createElement(React.Fragment, null)), tooltipText ? /*#__PURE__*/React.createElement(Tooltip, {
        id: `tooltip-link-${id}-${placement}`,
        place: tooltipLocationForPopup[placement],
        className: "m113-sticky-banner-card-tooltip"
      }, /*#__PURE__*/React.createElement("span", null, tooltipText)) : null);
    }
  };
  const renderHappinessMeterWidget = () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HappinessMeterWidget, {
    formUrl: "/HappinessMeterForm.aspx",
    analytics: eventsAnalytics,
    params: params,
    CTAVariation: true,
    customButton: true
  }), happinessMeterText ? /*#__PURE__*/React.createElement(Tooltip, {
    id: "Happiness-Meter-tooltip",
    place: `${rtl ? 'right' : 'left'}`,
    clickable: true,
    className: `m113-sticky-banner-card-tooltip`
  }, /*#__PURE__*/React.createElement("span", null, happinessMeterText)) : null);
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl
  }, leftLinks.length || centerLinks.length || rightLinks.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: `container m113-sticky-banner ${!isAlwaysBeVisible && scrollDown && !isPanelFocused ? 'collapsed' : ''}`,
    style: variables,
    ref: stickyBannerRef
  }, leftLinks.length ? /*#__PURE__*/React.createElement("div", {
    className: "m113-sticky-banner-bar left-bar"
  }, renderHappinessMeterWidget(), leftLinks.map(item => /*#__PURE__*/React.createElement(Fragment, {
    key: item.id
  }, renderLink(item, 'left')))) : null, centerLinks.length ? /*#__PURE__*/React.createElement("div", {
    className: "m113-sticky-banner-bar center-bar"
  }, centerLinks.map(item => /*#__PURE__*/React.createElement(Fragment, {
    key: item.id
  }, renderLink(item, 'center')))) : null, rightLinks.length ? /*#__PURE__*/React.createElement("div", {
    className: "m113-sticky-banner-bar right-bar"
  }, rightLinks.map(item => /*#__PURE__*/React.createElement(Fragment, {
    key: item.id
  }, renderLink(item, 'right')))) : null), iframeUrl && /*#__PURE__*/React.createElement(Modal, {
    isShown: isShown,
    hide: () => {
      if (isShown) {
        setLoading(true);
        // setIframeUrl('');
      }
      toggle();
    }
  }, loading ? /*#__PURE__*/React.createElement("div", {
    className: "modal-loader"
  }, /*#__PURE__*/React.createElement("div", {
    className: "loader-t1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "loadingio-spinner-rolling-arlp0sgzkfu"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ldio-fryp34cx4ij"
  }, /*#__PURE__*/React.createElement("div", null))))) : /*#__PURE__*/React.createElement(React.Fragment, null), /*#__PURE__*/React.createElement("iframe", {
    title: `${iframeUrl}-iframe`,
    width: "100%",
    onLoad: () => setLoading(false),
    frameBorder: "0",
    allowFullScreen: false,
    scrolling: "auto",
    className: "cta-variation-iframe",
    src: iframeUrl,
    "data-cy": "cta-variation-iframe"
  })), checkAccessibilitySettings() && AccessibilitySettingsComponent ? /*#__PURE__*/React.createElement(AccessibilitySettingsComponent, _extends({}, accessibilitySettings, {
    onAccessibilitySettingsClosed: toggleAccessibilitySettingsVisibility,
    visible: state.accessibilitySettingsVisible,
    activeLanguageCode: activeLanguage.languageCode,
    analytics: {
      textSizeAnalytics: analytics.textSizeAnalytics,
      contrastSwitchAnalytics: analytics.contrastSwitchAnalytics,
      readSpeakerAnalytics: analytics.readSpeakerAnalytics
    }
  })) : null) : null);
};
const StyledWrapper = _ref3 => {
  let {
    children,
    rtl
  } = _ref3;
  return /*#__PURE__*/React.createElement("div", {
    className: ` ${commonStyles}  ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
const commonStyles = "c1fy6usu";
const ltrStyles = "l1ld3uw2";
const rtlStyles = "rx03w1s";
export default CTAVariation;